import React, { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer } from "lucide-react";

const QR_LABEL_SIZE = 160;

function ToolLabel({ tool }) {
  return (
    <Card
      className="p-4 border-2 border-slate-900 bg-white text-center break-inside-avoid"
      data-testid={`qr-card-${tool.id}`}
    >
      <div className="flex items-center justify-center bg-white p-2">
        <QRCodeSVG value={tool.code} size={QR_LABEL_SIZE} level="H" includeMargin />
      </div>
      <p className="font-mono font-bold text-lg mt-2">{tool.code}</p>
      <p className="text-sm font-medium text-slate-900 truncate">{tool.name}</p>
      <p className="text-xs text-slate-600 truncate">{tool.model}</p>
      <p className="text-xs font-bold mt-1">
        {tool.vibration_m_s2.toFixed(1)} m/s²
      </p>
    </Card>
  );
}

export default function QRLabels() {
  const fetcher = useCallback(() => api.get("/tools").then((r) => r.data), []);
  const { data, loading } = useFetch(fetcher);
  const tools = data || [];
  const [selected, setSelected] = useState(new Set());

  // Default to selecting everything the first time tools load.
  useEffect(() => {
    if (data) setSelected(new Set(data.map((t) => t.id)));
  }, [data]);

  const toggle = (id) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id);
    else ns.add(id);
    setSelected(ns);
  };

  const toggleAll = () => {
    if (selected.size === tools.length) setSelected(new Set());
    else setSelected(new Set(tools.map((t) => t.id)));
  };

  const visible = tools.filter((t) => selected.has(t.id));

  return (
    <div className="space-y-6" data-testid="qr-labels-page">
      <div className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Print
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">
            QR labels
          </h1>
          <p className="text-slate-600 mt-2">
            Select tools, then print. Each label shows the tool code as a QR.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={toggleAll} data-testid="qr-toggle-all">
            {selected.size === tools.length ? "Deselect all" : "Select all"}
          </Button>
          <Button
            onClick={() => window.print()}
            className="gap-2 bg-slate-900 hover:bg-slate-800"
            data-testid="qr-print-btn"
          >
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      {loading && <p className="text-slate-500">Loading…</p>}

      {!loading && tools.length === 0 && (
        <Card className="p-10 text-center text-slate-500">
          Add tools first to print QR labels.
        </Card>
      )}

      {!loading && tools.length > 0 && (
        <>
          <Card className="p-4 no-print">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {tools.map((t) => (
                <label
                  key={t.id}
                  className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                >
                  <Checkbox
                    checked={selected.has(t.id)}
                    onCheckedChange={() => toggle(t.id)}
                    data-testid={`qr-select-${t.id}`}
                  />
                  <span className="text-sm">
                    <span className="font-mono">{t.code}</span> — {t.name}
                  </span>
                </label>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 print-grid">
            {visible.map((t) => (
              <ToolLabel key={t.id} tool={t} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
